import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { Icon, IconButton } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { DatePicker } from 'components/DatePickerV3/DatePicker.component';
import { Modal } from 'components/Modal/Modal.component';
import Tooltip from 'components/Tooltip';
import { Checkbox, TextArea } from 'components/form';
import { Row } from 'components/layout';

import { useResponsive } from 'hooks/useResponsive';
import useScrollOffset from 'hooks/useScrollOffset';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { ExtraCurricularAttributes } from 'lib/models/candidate';
import { REGEX } from 'lib/utils/regex';

import {
  formatDateToSave,
  getNumberOfWords,
  replaceLineBreaksWithBullet,
} from 'utils/format';
import DeleteModal from '../DeleteModal';
import styles from '../ProfileModal.common.module.scss';
import {
  generateExtraCurricularActivitiesDescription,
  getUpdateExtraCurricularValue,
} from './utils';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  selectedExtraCurricularInfo:
    | (ExtraCurricularAttributes & { isNew: boolean })
    | null;
}

export const AddExtraCurricularModal = memo(
  ({
    isOpen,
    onClose,
    isEdit,
    selectedExtraCurricularInfo,
  }: ProfileModalProps) => {
    const screens = useResponsive();
    const [t] = useTranslation('candidate.profile.modal');

    const { headers } = useRecoilValue(httpHeadersState);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRedoLoading, setIsRedoLoading] = useState(false);
    const [isTryAgainLoading, setIsTryAgainLoading] = useState(false);
    const [isGeneratedDescAccepted, setIsGeneratedDescAccepted] = useState<
      boolean | null
    >(null);
    const [generateDescSuccess, setGenerateDescSuccess] = useState<
      boolean | null
    >(null);
    const [suggestImproveSuccess, setSuggestImproveSuccess] = useState<
      boolean | null
    >(null);
    const [isGeneratedDescDismissed, setGeneratedDescDismissed] =
      useState(false);
    const [suggestImprovementLoading, setSuggestImprovementLoading] =
      useState(false);
    const [isDateError, setDateError] = useState<boolean | null>(false);
    const [abortController, setAbortController] = useState<AbortController>();

    const [candidateExtraCurricular, setCandidateExtraCurricular] = useState<
      ExtraCurricularAttributes & { isNew: boolean }
    >({
      id: uuidv4(),
      organisation_name: '',
      website: '',
      role: '',
      start_date: '',
      end_date: '',
      description: '',
      location: '',
      generated_description: [],
      generated_description_disable: false,
      isNew: true,
      presently_engaged: false,
    });
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
      null
    );

    const contentRef = useCallback((element: HTMLDivElement) => {
      if (element) {
        setContentElement(element);
      }
    }, []);
    const startDateDropdownRef = useRef<HTMLDivElement>(null);
    const endDateDropdownRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const { getScrollOffset } = useScrollOffset();
    const { updateCandidate } = useCandidateProfile();

    useEffect(() => {
      if (isEdit && selectedExtraCurricularInfo) {
        setCandidateExtraCurricular({
          ...selectedExtraCurricularInfo,
          isNew: false,
        });
      }
    }, [selectedExtraCurricularInfo, isEdit]);

    const isInvalid = useMemo(() => {
      if (candidateExtraCurricular) {
        const {
          start_date,
          end_date,
          organisation_name,
          website,
          role,
          description,
          location,
          presently_engaged,
        } = candidateExtraCurricular;
        if (
          !start_date ||
          !organisation_name ||
          !role ||
          !description ||
          !location
        )
          return true;
        else if (website && !REGEX.URL_CHECK.test(website)) return true;
        else if (!end_date && !presently_engaged) return true;
        else if (isDateError) return true;
        else return false;
      } else return true;
    }, [candidateExtraCurricular, isDateError]);

    const onSubmit = async () => {
      if (isInvalid) return;
      setIsSaving(true);
      const candidate = await getUpdateExtraCurricularValue(
        candidateExtraCurricular
      );
      await updateCandidate(candidate);
      setIsSaving(false);
      onClose();
    };

    const deleteExtraCurricularActivity = async () => {
      setIsDeleting(true);
      await updateCandidate(
        {
          extra_curricular_activities_attributes: {
            id: candidateExtraCurricular.id,
            _destroy: true,
          },
        },
        false,
        true
      );

      setIsDeleting(false);
      onClose();
    };

    const startDate = candidateExtraCurricular?.start_date
      ? new Date(candidateExtraCurricular.start_date)
      : '';
    const endDate = candidateExtraCurricular.end_date
      ? new Date(candidateExtraCurricular.end_date)
      : '';

    const [dots, setDots] = useState('');

    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const event = new Event('scroll');
      if (contentElement) {
        const topSpace = 12;
        const adjustment =
          contentElement.getBoundingClientRect().top + topSpace;
        const offset = getScrollOffset(
          dropdownRef,
          { current: contentElement },
          headerRef,
          adjustment
        );
        contentElement.scrollTop = offset;
        contentElement.dispatchEvent(event);
      }
    };

    const scrollToDropdownStartDate = () => {
      scrollToDropdown(startDateDropdownRef);
    };

    const scrollToDropdownEndDate = () => {
      scrollToDropdown(endDateDropdownRef);
    };
    useEffect(() => {
      const event = new Event('scroll-top');

      const handleScroll = () => {
        window.dispatchEvent(event);
      };

      if (contentElement) {
        contentElement.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (contentElement) {
          contentElement.removeEventListener('scroll', handleScroll);
        }
      };
    }, [contentElement]);

    useEffect(() => {
      let interval: NodeJS.Timeout | null = null;

      if (isLoading) {
        interval = setInterval(() => {
          setDots((prevDots) => {
            if (prevDots.length >= 3) {
              return '';
            } else {
              return prevDots + '.';
            }
          });
        }, 500);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }, [isLoading]);
    const animatedPlaceholder =
      t('common.animated-placeholder.generating') +
      dots +
      t('common.animated-placeholder.description');
    const handleInputFocus = () => {
      setGeneratedDescDismissed(true);
      setLoading(false);
    };

    const onGenerateDescription = async () => {
      setLoading(true);
      setIsGeneratedDescAccepted(false);
      const response = await generateExtraCurricularActivitiesDescription(
        candidateExtraCurricular,
        headers,
        setAbortController
      );
      if (response?.suggestions) {
        const { suggestions } = response;
        setCandidateExtraCurricular((info) => ({
          ...info,
          description: '',
          generated_description: suggestions,
        }));
        setGenerateDescSuccess(true);
        setLoading(false);
      }
    };

    const onAccept = () => {
      const description = candidateExtraCurricular.generated_description
        .map((pointer) => (pointer ? `• ${pointer}` : ''))
        .join('\n');
      setCandidateExtraCurricular((info) => ({
        ...info,
        description,
        generated_description: [],
        //once accepted we don't want to show the generated description again
        generated_description_disable: true,
      }));
      setIsGeneratedDescAccepted(true);
      setGenerateDescSuccess(null);
      setSuggestImproveSuccess(null);
    };

    const onRedo = async () => {
      setIsRedoLoading(true);
      try {
        const response = await generateExtraCurricularActivitiesDescription(
          candidateExtraCurricular,
          headers,
          undefined,
          !!suggestImproveSuccess
        );

        if (response?.suggestions) {
          const { suggestions } = response;
          setCandidateExtraCurricular((info) => ({
            ...info,
            description: suggestImproveSuccess ? info.description : '',
            generated_description: suggestions,
          }));

          setIsRedoLoading(false);
        } else {
          setIsRedoLoading(false);
        }
      } catch (error) {
        setIsRedoLoading(false);
      }
    };

    const onDismiss = () => {
      setCandidateExtraCurricular((info) => ({
        ...info,
        generated_description_disable: true,
      }));

      if (!candidateExtraCurricular?.description) {
        setIsGeneratedDescAccepted(false);
      }

      setGenerateDescSuccess(null);
      setGeneratedDescDismissed(true);
      setSuggestImproveSuccess(null);
    };

    const onSuggestImprovements = async () => {
      setSuggestImprovementLoading(true);
      try {
        const response = await generateExtraCurricularActivitiesDescription(
          candidateExtraCurricular,
          headers,
          undefined,
          true
        );

        if (response?.suggestions) {
          const { suggestions } = response;
          setCandidateExtraCurricular((info) => ({
            ...info,
            generated_description: suggestions,
            generated_description_disable: false,
          }));
          setGenerateDescSuccess(null);
          setSuggestImprovementLoading(false);
          setSuggestImproveSuccess(true);
        } else {
          setGenerateDescSuccess(null);

          setSuggestImproveSuccess(false);
          setSuggestImprovementLoading(false);
        }
      } catch (error) {
        setGenerateDescSuccess(null);

        setSuggestImproveSuccess(false);
        setSuggestImprovementLoading(false);
      }
    };

    const onTryAgain = async () => {
      setIsTryAgainLoading(true);
      if (suggestImproveSuccess === false) {
        await onSuggestImprovements();
      } else {
        await onGenerateDescription();
      }
      setIsTryAgainLoading(false);
    };
    const generateDescFailedCTAs = (
      <Row align="center" justify="space-between" className={styles.infoRow}>
        <Row columnGap={4} className={cn(styles.info, styles.infoFailed)}>
          <Icon
            className={styles.infoMarker}
            size={screens.sm ? 'small' : 'medium'}
            iconName="icon_magic-wand"
          />
          <p className={styles.ProfileModalLabel}>
            {t('common.could-not-generate')}
          </p>
        </Row>
        <Row columnGap={4}>
          <ButtonV3
            size="small"
            color="secondary"
            onClick={onTryAgain}
            isLoading={isTryAgainLoading}
          >
            {t('common.try-again')}
          </ButtonV3>
          <ButtonV3 size="small" color="secondary" onClick={onDismiss}>
            {t('common.dismiss')}
          </ButtonV3>
        </Row>
      </Row>
    );

    const generateDescSuccessCTAs = (
      <Row columnGap={4}>
        <ButtonV3 size="small" onClick={onAccept}>
          {t('common.accept')}
        </ButtonV3>
        <ButtonV3
          size="small"
          color="secondary"
          onClick={onRedo}
          isLoading={isRedoLoading}
        >
          {t('common.redo')}
        </ButtonV3>
        <ButtonV3 size="small" color="secondary" onClick={onDismiss}>
          {t('common.dismiss')}
        </ButtonV3>
      </Row>
    );

    const getTextArea = useMemo(() => {
      return (
        <TextArea
          value={
            isLoading
              ? animatedPlaceholder
              : candidateExtraCurricular?.description
          }
          onChange={(event) => {
            if (
              (candidateExtraCurricular?.description as string).length ===
              event.target.value.length + 1
            ) {
              setCandidateExtraCurricular((info) => ({
                ...info,
                description: event.target.value,
              }));
            } else if (event.target.value.endsWith('\n')) {
              setCandidateExtraCurricular((info) => ({
                ...info,
                description: event.target.value + '• ',
              }));
            } else {
              const description =
                candidateExtraCurricular?.description?.length === 0
                  ? '• ' + event.target.value
                  : event.target.value;
              setCandidateExtraCurricular((info) => ({
                ...info,
                description,
              }));
            }
          }}
          onFocus={() => {
            if (isLoading && abortController) {
              abortController?.abort();
              setLoading(false);
              setGeneratedDescDismissed(true);
            }
            if (candidateExtraCurricular?.description?.length === 0) {
              setCandidateExtraCurricular((info) => ({
                ...info,
                description: '• ',
              }));
            }
            handleInputFocus();
          }}
          onPaste={(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            event.preventDefault();
            const clipboardData = event.clipboardData;
            const clipboardValue = clipboardData.getData('Text');

            // Replace line breaks with bullet points
            let value = replaceLineBreaksWithBullet(clipboardValue);

            // Get the selection range
            const selectionStart = event.currentTarget.selectionStart;
            const selectionEnd = event.currentTarget.selectionEnd;

            // Add a bullet at the beginning if one doesn't already exist
            if (
              !value.startsWith('• ') &&
              selectionStart !== null &&
              (candidateExtraCurricular?.description as string).substring(
                selectionStart - 2,
                selectionStart
              ) != '• '
            ) {
              if (
                (candidateExtraCurricular?.description as string).substring(
                  selectionStart - 1,
                  selectionStart
                ) == '\n' ||
                selectionStart == 0
              ) {
                value = '• ' + value;
              }
            }

            if (selectionStart !== null && selectionEnd !== null) {
              // Get the text before and after the selected range
              const textBeforeSelection =
                (candidateExtraCurricular?.description as string).substring(
                  0,
                  selectionStart
                ) || '';
              const textAfterSelection =
                (candidateExtraCurricular?.description as string).substring(
                  selectionEnd
                ) || '';

              // Update details with the replaced text
              setCandidateExtraCurricular((info) => ({
                ...info,
                description: textBeforeSelection + value + textAfterSelection,
              }));
            }
          }}
          isDynamic={true}
          textAreaClassName={cn(styles.ProfileModalDescriptionBox, {
            [styles.loadingAnimation]: isLoading,
          })}
          isValid={
            !(isSubmitted && !candidateExtraCurricular.description)
              ? true
              : false
          }
        />
      );
    }, [
      candidateExtraCurricular.description,
      isLoading,
      isSubmitted,
      abortController,
    ]);

    useEffect(() => {
      const startDate = new Date(candidateExtraCurricular?.start_date);
      const endDate = new Date(candidateExtraCurricular?.end_date);
      if (
        candidateExtraCurricular?.start_date &&
        candidateExtraCurricular?.end_date &&
        endDate < startDate
      ) {
        setDateError(true);
      } else {
        setDateError(false);
      }
    }, [
      candidateExtraCurricular?.start_date,
      candidateExtraCurricular?.end_date,
    ]);

    return (
      <>
        <Modal
          isOpen={isOpen}
          onRequestClose={onClose}
          contentClassName={cn(styles.ProfileModal)}
        >
          <div className={styles.ProfileModalHeader} ref={headerRef}>
            {isEdit ? (
              <p>{t('extra-curricular-activity.edit')}</p>
            ) : (
              <p>{t('extra-curricular-activity.add')}</p>
            )}
            <IconButton
              onClick={onClose}
              className={styles.ProfileModalHeaderClose}
              iconName="close"
              size="small"
            />
            <IconButton
              onClick={onClose}
              className={styles.ProfileModalHeaderCloseMobile}
              iconName="arrow-button-left"
              size="small"
            />
          </div>
          <div className={styles.ProfileModalContent} ref={contentRef}>
            {isGeneratedDescDismissed || isGeneratedDescAccepted || isEdit ? (
              <></>
            ) : (
              <Row
                className={styles.hintDescription}
                wrap={false}
                columnGap={4}
              >
                <Icon
                  className={styles.infoMarker}
                  size={screens.sm ? 'small' : 'large'}
                  iconName="icon_info"
                />
                <p>{t('common.hint-description')}</p>
              </Row>
            )}
            <InputV2
              label={t('common.organisation.label')}
              placeholder={t('common.organisation.placeholder')}
              maxLength={160}
              value={candidateExtraCurricular?.organisation_name}
              onChange={(event) =>
                setCandidateExtraCurricular((info) => ({
                  ...info,
                  organisation_name: event.target.value,
                }))
              }
              className={styles.gap}
              isInvalid={
                isSubmitted && !candidateExtraCurricular.organisation_name
              }
            />
            <InputV2
              label={t('common.organisation-website.label')}
              placeholder={t('common.organisation-website.placeholder')}
              maxLength={160}
              value={candidateExtraCurricular?.website}
              onChange={(event) =>
                setCandidateExtraCurricular((info) => ({
                  ...info,
                  website: event.target.value,
                }))
              }
              showOptionalLabel={true}
              className={styles.gap}
              isInvalid={
                isSubmitted &&
                candidateExtraCurricular.website &&
                !REGEX.URL_CHECK.test(candidateExtraCurricular.website)
                  ? true
                  : false
              }
            />
            <InputV2
              label={t('common.role.label')}
              placeholder={t('common.role.placeholder')}
              maxLength={160}
              value={candidateExtraCurricular?.role}
              onChange={(event) =>
                setCandidateExtraCurricular((info) => ({
                  ...info,
                  role: event.target.value,
                }))
              }
              className={styles.gap}
              isInvalid={isSubmitted && !candidateExtraCurricular.role}
              showTabText={!screens.sm ? 'Generate description' : ''}
              onKeyDown={async (event) => {
                if (
                  event.key === 'Tab' &&
                  candidateExtraCurricular?.role &&
                  candidateExtraCurricular?.organisation_name &&
                  !screens.sm
                ) {
                  event.preventDefault();
                  setLoading(true);

                  const response =
                    await generateExtraCurricularActivitiesDescription(
                      candidateExtraCurricular,
                      headers,
                      setAbortController
                    );
                  if (response?.suggestions) {
                    const { suggestions } = response;
                    setCandidateExtraCurricular((info) => ({
                      ...info,
                      description: '',
                      generated_description: suggestions,
                    }));
                    setGenerateDescSuccess(true);
                    setLoading(false);
                  }
                }
              }}
            />
            <div className={cn(styles.ProfileModalBoxWrapper, styles.gap)}>
              <div className={styles.boxTitle}>
                <p className={styles.ProfileModalLabel}>
                  {t('common.description.label')}
                  <Tooltip
                    content={t('common.description.tooltip')}
                    position="top"
                    color="dark"
                    className={cn(styles.tooltipContainer, styles.translate25p)}
                  >
                    <Icon
                      iconName="icon_info"
                      className={styles.tooltipContainerIcon}
                      size="auto"
                    />
                  </Tooltip>
                </p>
              </div>
              <div className={styles.inputWrapper}>
                {generateDescSuccess !== null ? (
                  <>
                    <div
                      className={cn(styles.ProfileModalDescriptionBox, {
                        [styles.ProfileModalDescriptionBoxError]:
                          isSubmitted &&
                          !candidateExtraCurricular?.description &&
                          candidateExtraCurricular?.generated_description
                            ?.length > 0 &&
                          !isGeneratedDescAccepted,
                      })}
                    >
                      <ul className={styles.generatedDesc}>
                        {candidateExtraCurricular.generated_description?.map(
                          (pointer, index) =>
                            pointer ? (
                              <li key={index}>{pointer}</li>
                            ) : (
                              <li className={styles.empty} key={index}>
                                &nbsp;
                              </li>
                            )
                        )}
                      </ul>
                    </div>
                    {generateDescSuccess ? (
                      <Row
                        align="center"
                        justify="space-between"
                        className={styles.infoRow}
                      >
                        <Row className={styles.info} columnGap={4}>
                          <Icon
                            className={styles.infoMarkerSuccess}
                            size={screens.sm ? 'small' : 'medium'}
                            iconName="icon_magic-wand"
                          />
                          <p className={styles.ProfileModalLabel}>
                            {t('common.ai-generated-description')}
                          </p>
                        </Row>
                        {generateDescSuccessCTAs}
                      </Row>
                    ) : (
                      generateDescFailedCTAs
                    )}
                  </>
                ) : (
                  <>
                    {getTextArea}
                    {suggestImproveSuccess !== null ? (
                      suggestImproveSuccess ? (
                        <>
                          <Row
                            className={styles.info}
                            wrap={false}
                            columnGap={4}
                          >
                            <Icon
                              className={styles.infoMarkerSuccess}
                              size={screens.sm ? 'small' : 'medium'}
                              iconName="icon_magic-wand"
                            />
                            <p>{t('common.suggested-description')}</p>
                          </Row>
                          <div
                            className={cn(
                              styles.suggestedDescRow,
                              styles.suggestedContent
                            )}
                          >
                            <ul className={styles.suggestedDesc}>
                              {candidateExtraCurricular.generated_description?.map(
                                (pointer, index) =>
                                  pointer ? (
                                    <li key={index}>{pointer}</li>
                                  ) : (
                                    <li className={styles.empty} key={index}>
                                      &nbsp;
                                    </li>
                                  )
                              )}
                            </ul>
                          </div>

                          <div className={styles.suggestedDescRow}>
                            {generateDescSuccessCTAs}
                          </div>
                        </>
                      ) : (
                        generateDescFailedCTAs
                      )
                    ) : (
                      <Row className={styles.info} wrap={false} columnGap={8}>
                        {getNumberOfWords(
                          candidateExtraCurricular?.description as string
                        ) > 3 ? (
                          <></>
                        ) : (
                          <Tooltip
                            content={t('common.create-with-ai.tooltip')}
                            position="top"
                            xAlign="left"
                            color="dark"
                            className={cn(
                              styles.tooltipContainer,
                              styles.noTranslate,
                              styles.width286
                            )}
                            disabled={
                              candidateExtraCurricular?.role &&
                              candidateExtraCurricular?.organisation_name
                                ? true
                                : false
                            }
                          >
                            <ButtonV3
                              startIcon="icon_magic-wand"
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                if (
                                  !candidateExtraCurricular?.role ||
                                  !candidateExtraCurricular?.organisation_name ||
                                  isLoading ||
                                  suggestImprovementLoading
                                ) {
                                  event.preventDefault();
                                } else {
                                  onGenerateDescription();
                                }
                              }}
                              isLoading={isLoading}
                            >
                              {t('common.create-with-ai.label')}
                            </ButtonV3>
                          </Tooltip>
                        )}
                        <Tooltip
                          content={t('common.suggest-improvements.tooltip')}
                          position="top"
                          xAlign="right"
                          color="dark"
                          className={cn(
                            styles.tooltipContainer,
                            styles.noTranslate,
                            styles.suggestImprovementWidth,
                            {
                              [styles.firstButton]:
                                getNumberOfWords(
                                  candidateExtraCurricular?.description as string
                                ) > 3,
                            }
                          )}
                        >
                          <ButtonV3
                            size="small"
                            color="secondary"
                            startIcon="icon_bolt"
                            onClick={(event) => {
                              if (
                                candidateExtraCurricular?.role &&
                                candidateExtraCurricular?.organisation_name &&
                                getNumberOfWords(
                                  candidateExtraCurricular?.description as string
                                ) > 3
                              ) {
                                onSuggestImprovements();
                              } else {
                                event?.preventDefault();
                              }
                            }}
                            isLoading={suggestImprovementLoading}
                            disabled={isLoading}
                          >
                            {t('common.suggest-improvements.label')}
                          </ButtonV3>
                        </Tooltip>
                      </Row>
                    )}
                  </>
                )}
              </div>
            </div>

            <Checkbox
              checked={candidateExtraCurricular?.presently_engaged}
              onChange={() =>
                setCandidateExtraCurricular((info) => ({
                  ...info,
                  presently_engaged:
                    !candidateExtraCurricular?.presently_engaged,
                }))
              }
              className={cn(styles.ProfileModalCheckbox, styles.gap)}
            >
              {t('extra-curricular-activity.currently-engaged')}
            </Checkbox>
            <Row justify="space-between" className={cn(styles.dateContainer)}>
              <DatePicker
                label={t('common.start-date.label')}
                placeholder={t('common.start-date.placeholder')}
                value={startDate || null}
                onChange={(value) =>
                  setCandidateExtraCurricular((info) => ({
                    ...info,
                    start_date: formatDateToSave(value),
                  }))
                }
                className={cn(styles.date, styles.gap, {
                  [styles.errorGap]: isDateError,
                })}
                hasError={
                  (isSubmitted && !candidateExtraCurricular.start_date) ||
                  (isDateError && !candidateExtraCurricular?.presently_engaged)
                }
                ref={startDateDropdownRef}
                scrollToDropdown={scrollToDropdownStartDate}
                dateSelectorClassName={cn(styles.left, styles.bottom)}
                dateInputClassName={styles.textarea}
              />
              {candidateExtraCurricular?.presently_engaged ? (
                <div
                  className={cn(styles.date, styles.gap, styles.dateContainer)}
                >
                  <p className={cn(styles.dateDisabled)}>
                    {t('common.end-date.label')}
                  </p>
                  <p className={cn(styles.dateDisabledText)}>
                    {t('common.end-date.present')}
                  </p>
                  <Icon
                    iconName="icon_arrow-down"
                    className={styles.dateDisabledIcon}
                  />
                </div>
              ) : (
                <DatePicker
                  label={t('common.end-date.label')}
                  placeholder={t('common.end-date.placeholder')}
                  value={endDate || null}
                  onChange={(value) =>
                    setCandidateExtraCurricular((info) => ({
                      ...info,
                      end_date: formatDateToSave(value),
                    }))
                  }
                  className={cn(styles.date, styles.gap, {
                    [styles.errorGap]: isDateError,
                  })}
                  minDate={startDate || undefined}
                  hasError={
                    (isSubmitted &&
                      !candidateExtraCurricular.end_date &&
                      !candidateExtraCurricular.presently_engaged) ||
                    isDateError
                  }
                  ref={endDateDropdownRef}
                  scrollToDropdown={scrollToDropdownEndDate}
                  dateSelectorClassName={styles.bottom}
                  dateInputClassName={styles.textarea}
                />
              )}
            </Row>
            {isDateError && !candidateExtraCurricular?.presently_engaged ? (
              <>
                <div className={styles.errorMessage}>
                  <Icon
                    iconName="icon_info"
                    className={styles.errorMessageIcon}
                    size="auto"
                  />
                  <p>{t('common.end-date.error')}</p>{' '}
                </div>
                <div className={styles.gap}></div>
              </>
            ) : null}
            <InputV2
              label={t('common.location.label')}
              placeholder={t('common.location.placeholder')}
              maxLength={160}
              value={candidateExtraCurricular?.location}
              onChange={(event) =>
                setCandidateExtraCurricular((info) => ({
                  ...info,
                  location: event.target.value,
                }))
              }
              className={styles.gap}
              isInvalid={isSubmitted && !candidateExtraCurricular.location}
            />
          </div>
          <div
            className={cn(styles.ProfileModalAction, {
              [styles.singleCta]: !isEdit,
            })}
          >
            {isEdit && (
              <ButtonV3
                variant="text"
                color="secondary"
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
                className={styles.button}
                disabled={isDeleting}
                isLoading={isDeleting}
              >
                {t('common.delete')}
              </ButtonV3>
            )}
            <ButtonV3
              onClick={() => {
                setIsSubmitted(true);
                onSubmit();
              }}
              className={styles.button}
              disabled={isSaving}
              isLoading={isSaving}
            >
              {isEdit ? t('common.done') : t('extra-curricular-activity.add')}
            </ButtonV3>
          </div>
        </Modal>
        {isDeleteModalOpen && (
          <DeleteModal
            isOpen={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            onConfirm={deleteExtraCurricularActivity}
            isButtonLoading={isDeleting}
            type={t('extra-curricular-activity.title')}
          />
        )}
      </>
    );
  }
);

AddExtraCurricularModal.displayName = 'AddExtraCurricularModal';
